import { ChangeDetectionStrategy, Component } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, withLatestFrom } from 'rxjs/operators';

import { optionalTranslate } from '@language';
import {
    BaseNestedFieldDefinition,
    EnumValue,
    FieldType
} from '../../../../../core/field-definitions/entities/field-definition.entity';
import { ExtendedBaseProfileField } from '../../../../../core/profiles/entities/extended-profile-field.entity';
import { PartialRecord } from '../../../../../core/records/entities/partial-record.class';
import { FormSelectOption } from '../../../../../form-controls/entities/form-select-option.entity';
import { MultiFormSelectOptions } from '../../../../../form-controls/entities/multi-form-select-options.entity';
import { FormValueRequired } from '../../../../../form-controls/helpers/form-value-required';
import { multiFormSelectOptionWrapper } from '../../../../../form-controls/helpers/multi-form-select-options-wrapper-helper';
import { isNotUndefined } from '../../../../../helpers/typed-reactive-filters.helper';
import { FieldReadHelper } from '../../../../../profile-form/helpers/fields/field-read.helper';
import { BatchFieldReadHelper } from '../../../../helpers/fields/batch-field-read.helper';
import { ShBaseGenericInputFieldComponent } from '../../base-generic-input-field/base-generic-input-field.component';

@Component({
    standalone: false,
    selector: 'sh-list-field',
    templateUrl: './list-input-field.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShListInputFieldComponent extends ShBaseGenericInputFieldComponent {
    public childDefinition$!: Observable<BaseNestedFieldDefinition>;
    public enumOptions$!: Observable<FormSelectOption[]>;
    public selectOptions$!: Observable<MultiFormSelectOptions>;
    public searchTerm$: BehaviorSubject<string> = new BehaviorSubject('');

    public FieldType: typeof FieldType = FieldType;

    constructor(protected fieldHelper: FieldReadHelper) {
        super();
        this.initChildDefinitionStream();
        this.initListInputFieldStreams();
    }

    protected initChildDefinitionStream(): void {
        this.childDefinition$ = this.profileField$.pipe(
            map(profileField => profileField.FieldDefinition.Children),
            map(children => {
                if (!children || children.length !== 1) {
                    throw new Error(`ComplexListField should have exactly 1 child, but has ${children?.length}`);
                }
                return children[0];
            })
        );
    }

    private initListInputFieldStreams(): void {
        this.enumOptions$ = this.childDefinition$.pipe(
            filter(child => child.Type === FieldType.EnumField),
            map(child => child.Values),
            isNotUndefined(),
            filter(val => Array.isArray(val)),
            withLatestFrom(this.childDefinition$),
            map(([enumValues, field]) =>
                enumValues.map((enumValue: EnumValue) => ({
                    label: optionalTranslate(this.translateService, field.FlatKey, enumValue.Value.Label),
                    value: enumValue.Value.Label,
                    disabled: !enumValue.Active,
                    disabledInfoMsg: this.translateService.instant('form.enum.obsolete-value')
                }))
            )
        );

        this.selectOptions$ = this.enumOptions$.pipe(
            withLatestFrom(this.searchTerm$),
            map(([possibleValues, searchTerm]) => {
                const options = possibleValues.filter(value => value.label.includes(searchTerm));
                return multiFormSelectOptionWrapper(options);
            })
        );

        combineLatest([this.childDefinition$, this.control$]).subscribe(([childDefinition, control]) => {
            this.combineParentChildDefinitionSettings(childDefinition, control);
        });
    }

    init(
        profileField: ExtendedBaseProfileField<BaseNestedFieldDefinition>,
        records: PartialRecord[],
        control: UntypedFormControl
    ): void {
        const values = this.isBatchEdit
            ? BatchFieldReadHelper.getBatchFieldValue(profileField.FieldDefinition)
            : this.fieldHelper.getMergedListFieldValue(profileField.FieldDefinition, records);

        control.setValue(values);
    }

    combineParentChildDefinitionSettings(
        childDefinition: BaseNestedFieldDefinition,
        control: UntypedFormControl
    ): void {
        if (childDefinition.Required) {
            control.setValidators(FormValueRequired);
        }

        if (childDefinition.ReadOnly) {
            control.disable();
        }
    }

    onSearchTermChange(searchTerm: string): void {
        this.searchTerm$.next(searchTerm);
    }
}
